import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { ChildThemeConfig, ChildMode, AGE_CONFIGS, MemberDisplayConfig } from '../types/gamification';

interface ChildThemeContextType {
  currentMemberId: string | null;
  memberConfig: MemberDisplayConfig | null;
  setCurrentMember: (memberId: string | null) => void;
  updateMemberConfig: (memberId: string, config: Partial<ChildThemeConfig>) => Promise<void>;
  isChildMode: boolean;
  childMode: ChildMode;
  showCelebrations: boolean;
  fontSize: string;
}

const ChildThemeContext = createContext<ChildThemeContextType | undefined>(undefined);

export function ChildThemeProvider({ children }: { children: ReactNode }) {
  const [currentMemberId, setCurrentMemberId] = useState<string | null>(null);
  const [memberConfig, setMemberConfig] = useState<MemberDisplayConfig | null>(null);

  // Cargar y escuchar cambios de configuración del miembro en tiempo real
  useEffect(() => {
    if (!currentMemberId) {
      setMemberConfig(null);
      return;
    }

    const docRef = doc(db, 'memberDisplayConfigs', currentMemberId);
    
    // Suscribirse a cambios en tiempo real
    const unsubscribe = onSnapshot(
      docRef,
      async (docSnap) => {
        if (docSnap.exists()) {
          setMemberConfig(docSnap.data() as MemberDisplayConfig);
        } else {
          // Crear configuración por defecto (modo adulto)
          const defaultConfig: MemberDisplayConfig = {
            memberId: currentMemberId,
            childMode: 'adult',
            themeConfig: AGE_CONFIGS.adult,
            badges: [],
            streak: {
              memberId: currentMemberId,
              currentStreak: 0,
              longestStreak: 0,
              lastActivityDate: new Date()
            }
          };
          setMemberConfig(defaultConfig);
          await setDoc(docRef, defaultConfig);
        }
      },
      (error) => {
        console.error('Error listening to member config:', error);
      }
    );

    // Cleanup: desuscribirse cuando el componente se desmonte o cambie el memberId
    return () => unsubscribe();
  }, [currentMemberId]);

  const setCurrentMember = (memberId: string | null) => {
    setCurrentMemberId(memberId);
  };

  const updateMemberConfig = async (memberId: string, configUpdate: Partial<ChildThemeConfig>) => {
    try {
      const docRef = doc(db, 'memberDisplayConfigs', memberId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const current = docSnap.data() as MemberDisplayConfig;
        const updated = {
          ...current,
          themeConfig: {
            ...current.themeConfig,
            ...configUpdate
          }
        };
        await setDoc(docRef, updated);
        if (memberId === currentMemberId) {
          setMemberConfig(updated);
        }
      }
    } catch (error) {
      console.error('Error updating member config:', error);
    }
  };

  // Usar useMemo para asegurar que los valores se recalculen cuando memberConfig cambie
  // Forzado a 'adult' temporalmente para descontinuar el Modo Kids
  const childMode: ChildMode = 'adult';
  
  const isChildMode = false;
  
  const showCelebrations = useMemo(
    () => memberConfig?.themeConfig.celebrationsEnabled ?? false,
    [memberConfig]
  );
  
  const fontSize = useMemo(
    () => memberConfig?.themeConfig.fontSize ?? 'medium',
    [memberConfig]
  );

  // Memoizar el valor del contexto para evitar re-renders innecesarios
  const contextValue = useMemo(
    () => ({
      currentMemberId,
      memberConfig,
      setCurrentMember,
      updateMemberConfig,
      isChildMode,
      childMode,
      showCelebrations,
      fontSize,
    }),
    [currentMemberId, memberConfig, isChildMode, childMode, showCelebrations, fontSize]
  );

  return (
    <ChildThemeContext.Provider value={contextValue}>
      {children}
    </ChildThemeContext.Provider>
  );
}

export function useChildTheme() {
  const context = useContext(ChildThemeContext);
  if (context === undefined) {
    throw new Error('useChildTheme must be used within a ChildThemeProvider');
  }
  return context;
}
